import React from 'react';

/**
 * IntelliSecure brandmark — a geometric hexagon enclosing a
 * crosshair/reticle with an angular "signal" mark cutting through it.
 * Flat, editorial, no gradients. Inherits color via tone or explicit color.
 *
 * Reads as: intelligence graph (hex) + precision targeting (reticle)
 * + active signal (angled slash).
 *
 * Usage:
 *   <Brand size={32} />
 *   <Brand size={32} color="#fff" />
 *   <Brand size={32} tone="accent" />    // uses --accent-from
 *   <Brand size={32} tone="mono" />      // uses --text-primary
 */
export default function Brand({ size = 28, color, tone = 'accent', title = 'IntelliSecure' }) {
  const resolved =
    color ??
    (tone === 'mono'
      ? 'var(--text-primary)'
      : tone === 'on-accent'
      ? 'var(--text-on-accent)'
      : 'var(--accent-from)');

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      // title="" marks it decorative (e.g. inside a labelled button)
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* Outer hexagon — the intelligence graph */}
      <path
        d="M16 2.5 L27.1 8.75 L27.1 23.25 L16 29.5 L4.9 23.25 L4.9 8.75 Z"
        stroke={resolved}
        strokeWidth="1.3"
        strokeLinejoin="miter"
      />
      {/* Crosshair — vertical + horizontal reticle hairlines */}
      <line x1="16" y1="6"  x2="16" y2="12" stroke={resolved} strokeWidth="1.1" />
      <line x1="16" y1="20" x2="16" y2="26" stroke={resolved} strokeWidth="1.1" />
      <line x1="6.5"  y1="16" x2="12" y2="16" stroke={resolved} strokeWidth="1.1" />
      <line x1="20"   y1="16" x2="25.5" y2="16" stroke={resolved} strokeWidth="1.1" />
      {/* Inner reticle ring */}
      <circle cx="16" cy="16" r="3.8" stroke={resolved} strokeWidth="1.1" />
      {/* Signal slash — active signal cutting across */}
      <path
        d="M12.4 19.6 L19.6 12.4"
        stroke={resolved}
        strokeWidth="1.6"
        strokeLinecap="square"
      />
      {/* Signal node */}
      <rect x="18.7" y="11.5" width="2" height="2" fill={resolved} />
    </svg>
  );
}

/**
 * Brand lockup — icon + wordmark, inline.
 * Wordmark is set in the UI face (Switzer), semibold with slight negative
 * tracking, so the logo and the product read as one system.
 */
export function BrandLockup({
  size = 26,
  color,
  tone = 'accent',
  wordmarkColor = 'var(--text-primary)',
  subtitle,
  compact = false,
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        lineHeight: 1,
        textDecoration: 'none',
      }}
    >
      <Brand size={size} color={color} tone={tone} />
      {!compact && (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3 }}>
          <span
            style={{
              fontSize: Math.max(13, Math.round(size * 0.56)),
              fontWeight: 650,
              letterSpacing: '-0.01em',
              color: wordmarkColor,
              fontFamily: 'var(--font-sans)',
            }}
          >
            IntelliSecure
          </span>
          {subtitle && (
            <span
              style={{
                fontSize: 12,
                color: 'var(--text-muted)',
                fontWeight: 500,
              }}
            >
              {subtitle}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
